import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Loader2, Save, MessageSquare, Mail } from "lucide-react";

export default function AdminCommunications() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await authAxios.get("/settings");
    setSettings(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const update = (path, value) => {
    setSettings((s) => {
      const next = { ...s };
      if (path.includes(".")) {
        const [a, b] = path.split(".");
        next[a] = { ...next[a], [b]: value };
      } else next[path] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await authAxios.put("/settings", settings);
      setSettings(r.data);
      toast({ title: "Settings saved" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const testSms = async () => {
    if (!testTo) return;
    setBusy(true);
    try {
      const r = await authAxios.post("/settings/test-sms", { to_phone: testTo });
      toast({
        title: r.data.sent ? "Test SMS sent" : "Test SMS failed",
        description: r.data.sent ? `SID: ${r.data.sid}` : r.data.error,
        variant: r.data.sent ? "default" : "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const testEmailSend = async () => {
    if (!testEmail) return;
    setBusy(true);
    try {
      const r = await authAxios.post("/settings/test-email", { to_email: testEmail });
      toast({
        title: r.data.sent ? "Test email sent" : "Test email failed",
        description: r.data.sent ? `id: ${r.data.id}` : r.data.error,
        variant: r.data.sent ? "default" : "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return <div className="p-8 text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>;
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold text-slate-900">Communications</h1>
        <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white">
          {saving ? <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Saving…</> : <><Save className="mr-2 h-4 w-4" /> Save changes</>}
        </Button>
      </div>

      {/* Business contact */}
      <Section title="Business contact" subtitle="Used for booking notifications and email replies">
        <Row>
          <Field label="Business email" value={settings.business_email} onChange={(v) => update("business_email", v)} placeholder="cheersplumbing@outlook.com" />
          <Field label="Business phone (E.164)" value={settings.business_phone} onChange={(v) => update("business_phone", v)} placeholder="+61431189875" />
          <Field label="Display phone" value={settings.company_phone_display} onChange={(v) => update("company_phone_display", v)} placeholder="0431 189 875" />
        </Row>
      </Section>

      {/* SMS providers */}
      <Section title="SMS provider" subtitle="Select the default SMS gateway and configure credentials" icon={MessageSquare}>
        <Row>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Default SMS provider</Label>
            <Select value={settings.sms_provider} onValueChange={(v) => update("sms_provider", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="mobilemessage">MobileMessage (AU)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Row>

        <Sub title="Twilio">
          <Row>
            <Field label="Account SID" value={settings.twilio.account_sid} onChange={(v) => update("twilio.account_sid", v)} placeholder="AC…" />
            <Field label="Auth Token" value={settings.twilio.auth_token} onChange={(v) => update("twilio.auth_token", v)} placeholder="••••••••" type="password" />
            <Field label="From number (E.164)" value={settings.twilio.from_number} onChange={(v) => update("twilio.from_number", v)} placeholder="+61…" />
          </Row>
        </Sub>

        <Sub title="MobileMessage (Australian SMS gateway)">
          <Row>
            <Field label="Username / API username" value={settings.mobilemessage.username} onChange={(v) => update("mobilemessage.username", v)} placeholder="your-mm-username" />
            <Field label="Password / API key" value={settings.mobilemessage.password} onChange={(v) => update("mobilemessage.password", v)} placeholder="••••••••" type="password" />
            <Field label="Sender ID" value={settings.mobilemessage.sender_id} onChange={(v) => update("mobilemessage.sender_id", v)} placeholder="Cheerings" />
          </Row>
          <Row>
            <Field
              label="Inbound webhook token (recommended)"
              value={settings.mobilemessage.inbound_token || ""}
              onChange={(v) => update("mobilemessage.inbound_token", v)}
              placeholder="any long random string"
              type="password"
            />
            <div className="text-xs text-slate-500 leading-relaxed pt-1">
              To receive customer SMS replies in <strong>Admin → Conversations</strong>, log in to your
              MobileMessage account and set the <strong>Inbound URL</strong> to:
              <code className="block mt-1 px-2 py-1 bg-slate-100 rounded text-slate-700 break-all">
                {(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/mobilemessage/inbound
                {settings.mobilemessage.inbound_token ? `?token=${settings.mobilemessage.inbound_token}` : ""}
              </code>
              The token rejects spoofed traffic. Leave blank for an open endpoint (not recommended in production).
            </div>
          </Row>
        </Sub>

        <Sub title="Test SMS">
          <div className="flex flex-wrap gap-2">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="0431189875" className="flex-1 min-w-[200px]" />
            <Button onClick={testSms} disabled={busy || !testTo} variant="outline">Send test SMS</Button>
          </div>
        </Sub>
      </Section>

      {/* Email providers */}
      <Section title="Email provider" subtitle="Select the default email service and configure credentials" icon={Mail}>
        <Row>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Default email provider</Label>
            <Select value={settings.email_provider} onValueChange={(v) => update("email_provider", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="resend">Resend</SelectItem>
                <SelectItem value="sendgrid">SendGrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Row>

        <Sub title="Resend">
          <Row>
            <Field label="API key" value={settings.resend.api_key} onChange={(v) => update("resend.api_key", v)} placeholder="re_…" type="password" />
            <Field label="From email (verified sender)" value={settings.resend.from_email} onChange={(v) => update("resend.from_email", v)} placeholder="bookings@cheeringsplumbing.com.au" />
            <Field label="Reply-to email (Resend receiving address)" value={settings.resend.reply_to_email || ""} onChange={(v) => update("resend.reply_to_email", v)} placeholder="replies@inbound.cheeringsplumbing.com.au" />
          </Row>
          <Row>
            <Field
              label="Inbound webhook secret (recommended)"
              value={settings.resend.inbound_secret || ""}
              onChange={(v) => update("resend.inbound_secret", v)}
              placeholder="whsec_…"
              type="password"
            />
            <div className="text-xs text-slate-500 leading-relaxed pt-1">
              In Resend, enable Receiving for the reply-to domain/address, then add a webhook for the <strong>email.received</strong> event pointing at:
              <code className="block mt-1 px-2 py-1 bg-slate-100 rounded text-slate-700 break-all">
                {(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/email/inbound
              </code>
              Paste the signing secret here so we can reject spoofed requests. Customer email replies will then flow straight into your Conversations inbox.
              {!settings.resend.inbound_secret && (
                <span className="block mt-1 text-amber-600 font-semibold">
                  ⚠ No secret set — inbound endpoint is open. Set this in production.
                </span>
              )}
            </div>
          </Row>
        </Sub>

        <Sub title="SendGrid">
          <Row>
            <Field label="API key" value={settings.sendgrid.api_key} onChange={(v) => update("sendgrid.api_key", v)} placeholder="SG.…" type="password" />
            <Field label="From email (verified sender)" value={settings.sendgrid.from_email} onChange={(v) => update("sendgrid.from_email", v)} placeholder="bookings@cheeringsplumbing.com.au" />
            <Field label="Reply-to email" value={settings.sendgrid.reply_to_email || ""} onChange={(v) => update("sendgrid.reply_to_email", v)} placeholder="replies@cheeringsplumbing.com.au" />
          </Row>
        </Sub>

        <Sub title="Test email">
          <div className="flex flex-wrap gap-2">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="flex-1 min-w-[200px]" />
            <Button onClick={testEmailSend} disabled={busy || !testEmail} variant="outline">Send test email</Button>
          </div>
        </Sub>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={18} className="text-sky-600" />}
        <div>
          <h2 className="font-extrabold text-slate-900">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-5 pt-5 border-t border-slate-100">
      <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">{title}</div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type} className="mt-1 h-10" />
    </div>
  );
}
